export interface CoinInfo {
  id: string;
  symbol: string;
  name: string;
  color: string;
}

export const SUPPORTED_COINS: CoinInfo[] = [
  { id: 'bitcoin',      symbol: 'BTC',  name: 'Bitcoin',   color: '#F7931A' },
  { id: 'ethereum',     symbol: 'ETH',  name: 'Ethereum',  color: '#627EEA' },
  { id: 'solana',       symbol: 'SOL',  name: 'Solana',    color: '#9945FF' },
  { id: 'binancecoin',  symbol: 'BNB',  name: 'BNB',       color: '#F3BA2F' },
  { id: 'cardano',      symbol: 'ADA',  name: 'Cardano',   color: '#0033AD' },
  { id: 'polkadot',     symbol: 'DOT',  name: 'Polkadot',  color: '#E6007A' },
  { id: 'avalanche-2',  symbol: 'AVAX', name: 'Avalanche', color: '#E84142' },
  { id: 'chainlink',    symbol: 'LINK', name: 'Chainlink', color: '#2A5ADA' },
  { id: 'ripple',       symbol: 'XRP',  name: 'XRP',       color: '#00AAE4' },
];

export const COIN_BY_ID = Object.fromEntries(
  SUPPORTED_COINS.map((c) => [c.id, c])
) as Record<string, CoinInfo>;

export type TransactionType = 'BUY' | 'SELL' | 'TRANSFER';

/**
 * How a transaction's FX rates were obtained.
 *
 * - `spot-at-entry` — today's spot rate, captured when the row was saved. NOT
 *   the rate for the transaction's own date. Provisional: these rows are the
 *   ones a later date-correct lookup re-resolves.
 * - `rba` / `ecb` — a rate looked up for the transaction's own date.
 * - `manual` — entered by the user; never overwritten automatically.
 */
export type FxSource = 'spot-at-entry' | 'rba' | 'ecb' | 'manual';

export interface Transaction {
  id: string;
  coinId: string;
  symbol: string;
  name: string;
  type: TransactionType;
  /**
   * Price per unit in USD. DERIVED CACHE of `entryAmount * fxRateUsd`, kept as
   * a stored column because ~26 files read it for portfolio maths, charts and
   * planning. `entryAmount` + `entryCurrency` + `fxRateUsd` are the source of
   * truth; this is regenerable from them.
   *
   * Rows predating FX capture have no entry fields — for those, this IS the
   * only record of the price.
   */
  price: number;
  amount: number;
  date: string;
  timestamp: number;
  wallet?: string;        // BUY/SELL only
  fromWallet?: string;    // TRANSFER source
  toWallet?: string;      // TRANSFER destination
  networkFee?: number;    // TRANSFER optional fee, in USD (converted from the entry currency on save)
  notes?: string;         // TRANSFER optional free-text notes

  // ── FX capture ────────────────────────────────────────────────────────────
  // All optional: rows created before this existed have none of them, and a row
  // whose rates could not be resolved records the entry pair without the rates.
  // Absence is meaningful — never default these to 1.

  /** Price per unit exactly as the user typed it, in `entryCurrency`. */
  entryAmount?: number;
  /** ISO 4217 code the user typed `entryAmount` in (the display currency at entry). */
  entryCurrency?: string;
  /** USD per 1 unit of `entryCurrency`, on `fxRateDate`. */
  fxRateUsd?: number;
  /** AUD per 1 unit of `entryCurrency`, on `fxRateDate`. Both legs share one date. */
  fxRateAud?: number;
  /** The date `fxRateUsd` / `fxRateAud` actually apply to — NOT necessarily `date`. */
  fxRateDate?: string;
  /** How those rates were obtained. See `FxSource`. */
  fxSource?: FxSource;

  // ── Transaction fee (BUY / SELL) ──────────────────────────────────────────
  // Brokerage, exchange commission, spread — the incidental cost of the trade
  // itself. A fee on ACQUISITION joins the cost base; a fee on DISPOSAL reduces
  // proceeds. Either way it reduces the taxable gain.
  //
  // FIAT ONLY in v1: `feeCurrency` is an ISO 4217 code. A fee paid IN the asset
  // (0.0004 BTC of network fee) is deliberately unsupported, because valuing it
  // needs a second price lookup — the coin's own price on the transaction date —
  // which is a different problem from an FX conversion.
  //
  // Absent on every row created before this existed. Absence means NO FEE, and
  // is treated as zero everywhere, so every pre-existing figure is unchanged.

  /**
   * Fee exactly as the user typed it, in `feeCurrency`. SOURCE OF TRUTH: never
   * overwritten with a converted value, so the rate can always be re-resolved.
   */
  fee?: number;
  /** ISO 4217 code `fee` is denominated in (the display currency at entry). */
  feeCurrency?: string;
  /**
   * DERIVED CACHE of `fee` in USD, converted at `feeCurrency`'s rate for the
   * transaction's own date. Stored for the same reason `price` is: the portfolio
   * maths is synchronous and USD-denominated. Regenerable from `fee` +
   * `feeCurrency` + `date`.
   */
  feeUsd?: number;
  /**
   * DERIVED CACHE of `fee` in AUD, same date. Carried separately rather than
   * derived from `feeUsd` for exactly the reason `fxRateAud` is: going via USD
   * would push the fee through a second conversion at a second date.
   */
  feeAud?: number;
}

export interface CoinPrice {
  usd: number;
  usd_24h_change: number;
}

export type PriceMap = Record<string, CoinPrice>;

/**
 * A holding's money figures in the user's DISPLAY currency, alongside the USD
 * figures they were derived from.
 *
 * Three classes of figure, converted three different ways — the distinction is
 * the whole point of this block:
 *
 *   A. HISTORICAL COST (`totalCost`, `avgBuyPrice`) — each transaction converted
 *      at ITS OWN date's rate. This is what the money actually was. Converting a
 *      2023 purchase at today's rate reports a number the user never paid.
 *   B. CURRENT VALUE (`totalValue`, `currentPrice`) — today's rate, because
 *      there is no historical rate for what something is worth right now.
 *   C. THE COMPARISON (`pnl`, `pnlPercent`) — B minus A, so it necessarily
 *      INCLUDES currency movement. For a non-USD investor that is economically
 *      correct and is how the CGT engine already reports gains: if the AUD fell
 *      while you held, you really did gain in AUD. The old single-rate conversion
 *      silently suppressed that. The UI discloses it rather than hiding it.
 *
 * Parallel to the USD fields rather than replacing them, so the ~44 existing read
 * sites change meaning only where they were deliberately migrated.
 */
export interface HoldingDisplay {
  totalCost: number;     // class A
  avgBuyPrice: number;   // class A
  totalValue: number;    // class B
  currentPrice: number;  // class B
  pnl: number;           // class C
  pnlPercent: number;    // class C
  /** True when any transaction's date had no published rate and used today's. */
  fxEstimated: boolean;
}

export interface Holding {
  coinId: string;
  symbol: string;
  name: string;
  color: string;
  amount: number;
  avgBuyPrice: number;
  currentPrice: number;
  totalValue: number;
  totalCost: number;
  pnl: number;
  pnlPercent: number;
  allocation: number;
  change24h: number;
  assetType: 'crypto' | 'metal' | 'stock' | 'etf';
  /** The same money figures in the display currency. See HoldingDisplay. */
  display: HoldingDisplay;
}

export interface PortfolioStats {
  totalValue: number;
  totalCost: number;
  totalPnl: number;
  totalPnlPercent: number;
  change24h: number;
  change24hValue: number;
  assetCount: number;
  /** Display-currency totals, summed from the per-holding display figures. */
  display: {
    totalValue: number;      // class B
    totalCost: number;       // class A
    totalPnl: number;        // class C
    totalPnlPercent: number; // class C
    change24hValue: number;  // class B
    fxEstimated: boolean;
  };
}

export interface PortfolioSnapshot {
  date: string;
  value: number;
}

export interface CoinListItem {
  id: string;
  symbol: string;       // uppercase
  name: string;
  image: string;        // icon URL from CoinGecko (empty for metals/stocks)
  marketCapRank: number;
  currentPrice: number;
  marketCap: number;
  volume24h: number;
  high24h: number;
  low24h: number;
  exchange?: string;    // e.g. 'NASDAQ', 'NYSE', 'ASX' — for stocks/ETFs
}

export type TimeRange = '24H' | '1W' | '1M' | '3M' | '6M' | '1Y' | 'MAX';

export interface PricePoint {
  timestamp: number;
  price: number;
}


export interface MarketCoin {
  id: string;
  symbol: string;
  name: string;
  image: string;
  marketCapRank: number;
  currentPrice: number;
  marketCap: number;
  volume24h: number;
  change24h: number;
  change7d: number;
  sparkline7d: number[];  // ~168 hourly price points
}

export type SortKey = keyof Holding;
export type SortDir = 'asc' | 'desc';

export interface CurrencyOption {
  code: string;
  name: string;
}

export const CURRENCIES: CurrencyOption[] = [
  { code: 'USD', name: 'US Dollar'          },
  { code: 'AUD', name: 'Australian Dollar'  },
  { code: 'EUR', name: 'Euro'               },
  { code: 'GBP', name: 'British Pound'      },
  { code: 'JPY', name: 'Japanese Yen'       },
  { code: 'CAD', name: 'Canadian Dollar'    },
];

/**
 * The display currency a user gets before they choose one.
 *
 * AUD, because the whole tax side of this app is Australian — the CGT engine
 * computes in AUD, the reports are for the ATO, and the FX chain is built around
 * the RBA's daily rates. A new user landing in USD would see every figure in a
 * currency their tax return cannot use.
 *
 * Only a default: the picker still offers the full `CURRENCIES` list, and an
 * existing preference always wins. Deliberately NOT `CURRENCIES[0]`, so the
 * dropdown's display order and the default can change independently.
 */
export const DEFAULT_CURRENCY = 'AUD';

// ── Wallet connect ────────────────────────────────────────────────────────────

export type NetworkId = 'ethereum' | 'bitcoin' | 'solana' | 'bnb' | 'polygon';

export interface WalletBalance {
  symbol: string;
  name: string;
  coinId: string;
  amount: number;
  priceUsd: number;
  valueUsd: number;
}

export interface ConnectedWallet {
  id: string;
  network: NetworkId;
  address: string;
  connectedAt: number;
  lastSynced: number | null;
  balances: WalletBalance[];
  totalValueUsd: number;
}
